import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, YStack } from 'tamagui';

import PSMobileInput from '@/components/ui/PSMobileInput';

/**
 * ── /components/ps-mobile-input ──────────────────────────────────────────
 *
 * PSMobileInput — the Indian mobile-number field: live (phone-pad,
 * digits-only, capped at 10, the same validate-on-blur / clear-on-focus
 * rhythm PSInput runs), error, and disabled. Real call sites: auth/mobile.tsx
 * (the flow's entry step — live, `autoFocus`, submits the OTP request) and
 * auth/details.tsx (the same field shown back read-only once verified, while
 * the user fills in their name below it). The component's own docstring
 * covers five things in depth — why this isn't `PSInput type="mobile"`, why
 * it's one bordered frame rather than two boxes, why the inner `Input` is
 * `unstyled`, why the dial code is display-only with input RESTRICTED rather
 * than validated, and the focus/blur handoff — this preview exists to make
 * the two trickiest input-restriction behaviors actually testable rather
 * than just described.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSMobileInputPreview() {
  const [mobile, setMobile] = useState('');
  const [mobileError, setMobileError] = useState<string | undefined>();

  return (
    <>
      <Head>
        <title>PSMobileInput · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* live — the exact rhythm auth/mobile.tsx runs: clear the error on
            focus, judge validity on blur. The atom only restricts INPUT
            (phone-pad keyboard, digits-only, capped at 10 digits); the
            screen owns the "is this 10 digits" judgment and the message,
            same split as PSInput. Two behaviors worth actually exercising
            here rather than trusting the docstring:
              - type an 11th digit into an already-full field — refused
                outright, the number doesn't scroll or shift.
              - paste "+91 98765 43210" (or "098765 43210", or
                "919876543210") — bulk arrival keeps the LAST 10 digits, so
                whatever country/trunk code came along in the paste is
                stripped instead of corrupting the number. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — phone-pad, digits-only, capped at 10 (auth/mobile.tsx);
              blur under 10 digits for the error, focus to clear it. Try typing
              an 11th digit (refused) vs pasting &quot;+91 98765 43210&quot;
              (bulk paste keeps the last 10 digits, dropping the country code)
            </Text>
            <PSMobileInput
              value={mobile}
              onChangeText={setMobile}
              placeholder="9876543210"
              error={mobileError}
              onFocus={() => setMobileError(undefined)}
              onBlur={() =>
                setMobileError(
                  mobile.length === 10
                    ? undefined
                    : 'Enter a 10-digit mobile number'
                )
              }
            />
          </YStack>

          {/* error — border AND the +91 divider flip to $error together, as
            ONE object (see the component's point 2: a purple ring with a
            grey seam through it would read as two controls). Message
            renders below, same placement as PSInput's error. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              error — frame border AND the +91 divider go red together, one
              object not two
            </Text>
            <PSMobileInput
              value="98765"
              onChangeText={() => {}}
              error="Enter a 10-digit mobile number"
            />
          </YStack>

          {/* disabled — auth/details.tsx's read-only mobile row: the number
            the user just verified, shown back to them (whole field dimmed,
            prefix included) while they fill in their name below it. That
            screen derives the bare digits with `user?.mobile.slice(-10)!`
            since the stored value already carries the dial code — the same
            "keep the last 10" shape point 4 documents for a bulk paste. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              disabled — auth/details.tsx&apos;s read-only mobile row; whole
              field dimmed, prefix included, not editable
            </Text>
            <PSMobileInput
              value="9876543210"
              onChangeText={() => {}}
              disabled
            />
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
